import { AddIcon } from "@minket/brand/icons/add";
import { ContactsIcon } from "@minket/brand/icons/contacts";
import { UploadIcon } from "@minket/brand/icons/upload";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { fieldLine } from "@minket/ui/lib/styles";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, useState } from "react";
import { ContactLedger, contactColumns } from "@/components/audience/contact-ledger";
import { AudienceFacts } from "@/components/audience/facts";
import { contactStatus, OnDemand, Select, statusLabels, usePager } from "@/components/audience/kit";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { QueryBar, QueryField } from "@/components/page/query-bar";
import { ProjectScope } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/contacts/")({
  component: Contacts,
});

const loadImport = () => import("@/components/audience/contact-import.ondemand");
const ImportDialog = lazy(() => loadImport().then((m) => ({ default: m.ImportDialog })));
const loadAdd = () => import("@/components/audience/contact-add.ondemand");
const AddContactDialog = lazy(() => loadAdd().then((m) => ({ default: m.AddContactDialog })));

const will = "Each contact will be listed here with their status, consent and engagement.";
const statuses = [
  ["", "Any status"],
  ...Object.keys(statusLabels).map((key) => [key, contactStatus(key).label] as const),
] as const;

function Contacts() {
  const { project } = useWorkspace();
  const canManage = useCan("audience.manage");
  const importing = { label: "Import contacts", icon: UploadIcon };
  return (
    <Page
      title="Contacts"
      action={
        !project ? (
          <UnavailableAction {...importing} reason="Needs a project" />
        ) : !canManage ? (
          <UnavailableAction {...importing} reason="Viewers cannot import contacts" />
        ) : (
          <>
            <OnDemand
              load={loadAdd}
              button={(props) => (
                <Button variant="outline" {...props}>
                  <AddIcon size={20} className="size-5" />
                  Add contact
                </Button>
              )}
              dialog={(props) => <AddContactDialog projectId={project.id} {...props} />}
            />
            <OnDemand
              load={loadImport}
              button={(props) => (
                <Button {...props}>
                  <UploadIcon size={20} className="size-5" />
                  Import contacts
                </Button>
              )}
              dialog={(props) => <ImportDialog projectId={project.id} {...props} />}
            />
          </>
        )
      }
    >
      <ProjectScope
        will={will}
        icon={<ContactsIcon />}
        preview={(busy) => <GhostTable columns={contactColumns} label="contacts" busy={busy} />}
      >
        {(current) => <ContactList projectId={current.id} canManage={canManage} />}
      </ProjectScope>
    </Page>
  );
}

function ContactList({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState<{ email?: string; status?: string }>({});
  const pager = usePager();
  const query = useQuery(
    apiQuery(endpoints.contacts, { projectId }, { ...search, after: pager.after, limit: 25 }),
  );
  const filtered = Object.keys(search).length > 0;
  const preview = (busy: boolean) => (
    <GhostTable columns={contactColumns} label="contacts" busy={busy} />
  );
  const clear = () => {
    setEmail("");
    setStatus("");
    setSearch({});
    pager.reset();
  };
  return (
    <div className="grid min-w-0 content-start gap-5">
      <QueryBar
        label="Search contacts"
        onSearch={() => {
          setSearch({ ...(email ? { email } : {}), ...(status ? { status } : {}) });
          pager.reset();
        }}
        onClear={filtered ? clear : undefined}
      >
        <QueryField label="Address contains">
          {(control) => (
            <input
              {...control}
              value={email}
              placeholder="@example.com"
              onChange={(event) => setEmail(event.target.value)}
              className={fieldLine}
            />
          )}
        </QueryField>
        <QueryField label="Status">
          {(control) => (
            <Select {...control} value={status} onChange={setStatus} options={statuses} />
          )}
        </QueryField>
      </QueryBar>
      <AudienceFacts projectId={projectId} canManage={canManage} />
      <QueryView query={query} noun="contacts" preview={preview}>
        {(page) =>
          page.data.length === 0 ? (
            filtered ? (
              <EmptyState
                data-state="empty"
                icon={<ContactsIcon />}
                title="No contacts match these filters"
                description="Change a filter, or clear them to see every contact."
              >
                <Button variant="outline" onClick={clear}>
                  Clear filters
                </Button>
              </EmptyState>
            ) : (
              <EmptyState
                data-state="empty"
                icon={<ContactsIcon />}
                title="No contacts yet"
                description="Import a CSV, or create contacts from the API; each one keeps a ledger of what they agreed to."
                preview={preview(false)}
              />
            )
          ) : (
            <ContactLedger projectId={projectId} page={page} pager={pager} canManage={canManage} />
          )
        }
      </QueryView>
    </div>
  );
}
